import { Unsubscribe } from '@reduxjs/toolkit';
import { addNotification, NotificationType, updateMyFleetFileAcidList } from '../app/state/slices/global-slice';
import { store, subscribeStoreWithSelector } from '../app/state/store';
import { parseMyFleetFile } from '../components/my-fleet/my-fleet-parser';
import { MyFleetAcidListItem } from '../components/my-fleet/types';
import { MyFleetAcidListSource, MyFleetMode, MyFleetSettings } from '../udView/udview';
import { Actor } from './actor';
import { MessageType } from './actor-messages';
import { getDirectoryDb } from './directory-db';

const UPDATE_INTERVAL_SECONDS = 60;

/**
 * Syncs my fleet with the worker
 */
export class MyFleetSync {
    private fileUpdateInterval: number | null = null;
    private lastMyFleetFileUpdateTimestamp: number | null = null;
    private subscriptions: Unsubscribe[] = [];

    constructor(private readonly actor: Actor) {
        // prettier-ignore
        this.subscriptions = [
            subscribeStoreWithSelector((x) => x.settings.myFleetMode, this.onMyFleetModeChanged.bind(this)),
            subscribeStoreWithSelector((x) => x.settings.myFleetAirlines, this.onUpdateMyFleetFor(MyFleetMode.airline).bind(this)),
            subscribeStoreWithSelector((x) => x.settings.myFleetAirlinesApplyFlightNumberPattern, this.onUpdateMyFleetFor(MyFleetMode.airline).bind(this)),
            subscribeStoreWithSelector((x) => x.settings.myFleetAirport, this.onUpdateMyFleetFor(MyFleetMode.airport).bind(this)),
            subscribeStoreWithSelector((x) => x.settings.myFleetAirportMode, this.onUpdateMyFleetFor(MyFleetMode.airport).bind(this)),
            subscribeStoreWithSelector((x) => x.settings.myFleetAcidListSource, this.onUpdateMyFleetFor(MyFleetMode.acid).bind(this)),
            subscribeStoreWithSelector((x) => x.settings.myFleetAcidList, this.onUpdateMyFleetFor(MyFleetMode.acid).bind(this)),
            subscribeStoreWithSelector((x) => x.settings.myFleetDeskFilter, this.onUpdateMyFleetFor('all').bind(this))
        ];
    }

    finalize() {
        this.clearMyFleetUpdateLoop();
        this.subscriptions.forEach((x) => x());
        this.subscriptions = [];
    }

    private async onMyFleetModeChanged(mode: MyFleetMode) {
        const settings = store.getState().settings;
        if (mode === MyFleetMode.acid && settings.myFleetAcidListSource === MyFleetAcidListSource.file) {
            this.clearMyFleetUpdateLoop();
            // Run immediately
            // TODO: separate responsibilities inside this method
            await this.fileUpdateLoop();
            // File update loop will send my fleet update message to the worker
            // and the start the loop
            this.fileUpdateInterval = self.setInterval(this.fileUpdateLoop.bind(this), UPDATE_INTERVAL_SECONDS * 1000);
        } else {
            // stop file loop
            this.clearMyFleetUpdateLoop();
            // send update to the worker
            this.sendMyFleetUpdated();
        }
    }

    clearMyFleetUpdateLoop() {
        if (this.fileUpdateInterval) {
            self.clearInterval(this.fileUpdateInterval);
            this.fileUpdateInterval = null;
        }
        // Reset the last checked file timestamp
        this.lastMyFleetFileUpdateTimestamp = null;
    }

    async myFleetFileDirectoryChanged() {
        const state = store.getState();
        if (state.settings.myFleetMode === MyFleetMode.acid && state.settings.myFleetAcidListSource === MyFleetAcidListSource.file) {
            // clear old interval
            this.clearMyFleetUpdateLoop();
            // run the loop once immediately
            await this.fileUpdateLoop();
            // send my fleet updated
            this.sendMyFleetUpdated();
            // start the loop again
            this.fileUpdateInterval = self.setInterval(this.fileUpdateLoop.bind(this), UPDATE_INTERVAL_SECONDS * 1000);
        }
    }

    private onUpdateMyFleetFor(mode: MyFleetMode | 'all') {
        return (() => {
            if (mode === 'all' || store.getState().settings.myFleetMode === mode) {
                this.sendMyFleetUpdated();
            }
        }).bind(this);
    }

    private async fileUpdateLoop() {
        const db = await getDirectoryDb();
        const handle = await db.get('file-handles', 'my-fleet-file');
        if (handle && handle.kind === 'file') {
            try {
                const file = await handle.getFile();
                if (this.lastMyFleetFileUpdateTimestamp === file.lastModified) {
                    return;
                }
                const text = await file.text();
                const items = parseMyFleetFile(text);

                // Put last checked time in redux so we can display it nicely in the UI (just a gimmick tho)
                this.lastMyFleetFileUpdateTimestamp = file.lastModified;
                store.dispatch(updateMyFleetFileAcidList(items));
            } catch {
                store.dispatch(
                    addNotification({
                        type: NotificationType.myFileFileParseError,
                        closable: true
                    })
                );
                return;
            }
            // Send update to the web worker
            this.sendMyFleetUpdated();
        }
    }

    private sendMyFleetUpdated() {
        this.actor.sendAsync({
            type: MessageType.myFleetChanged,
            data: this.getMyFleetSettings()
        });
    }

    public getMyFleetSettings(): MyFleetSettings {
        const state = store.getState();
        const appSettings = state.settings;
        const mode = appSettings.myFleetMode;

        if (mode === MyFleetMode.disabled) {
            return {
                mode: MyFleetMode.disabled
            };
        } else if (mode === MyFleetMode.airline) {
            return {
                mode: MyFleetMode.airline,
                airlines: appSettings.myFleetAirlines,
                applyFlightNumberPattern: appSettings.myFleetAirlinesApplyFlightNumberPattern,
                deskFilter: appSettings.myFleetDeskFilter
            };
        } else if (mode === MyFleetMode.airport) {
            return {
                mode: MyFleetMode.airport,
                airport: appSettings.myFleetAirport,
                airportMode: appSettings.myFleetAirportMode,
                deskFilter: appSettings.myFleetDeskFilter
            };
        } else if (mode === MyFleetMode.acid) {
            const acidSource = appSettings.myFleetAcidListSource;
            let acids: MyFleetAcidListItem[] = [];
            switch (acidSource) {
                case MyFleetAcidListSource.file:
                    acids = state.global.myFleetFileAcidList;
                    break;
                case MyFleetAcidListSource.manual:
                    acids = appSettings.myFleetAcidList;
                    break;
                case MyFleetAcidListSource.desktopInterface:
                    // TODO: desktop integration
                    break;
            }
            return {
                mode: MyFleetMode.acid,
                acids: acids,
                deskFilter: appSettings.myFleetDeskFilter
            };
        } else {
            throw Error('Unknown my fleet mode.');
        }
    }
}
